import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { User, LogOut, Shield, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";

const UserMenu: React.FC = () => {
  const { user, logout, isLoading } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  if (isLoading || !user) {
    return null;
  }

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <div className="relative z-50" ref={menuRef}>
      {/* User Info Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-all duration-200"
      >
        <User size={20} />
        <span className="text-sm font-medium">
          {user.full_name || user.username}
        </span>
        {user.is_superuser && (
          <Shield size={16} className="text-orange-600 dark:text-orange-400" />
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-64 bg-white/90 dark:bg-neutral-800/90 backdrop-blur-sm rounded-xl shadow-soft z-[100] border border-orange-200/50 dark:border-neutral-700/50"
          onClick={(e) => e.stopPropagation()}
        >
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
            <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
              {user.full_name || user.username}
            </p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {user.email}
            </p>
            {user.is_superuser && (
              <p className="text-xs text-orange-600 dark:text-orange-400 font-medium mt-1">
                Administrator
              </p>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {/* Home Link */}
            <button
              onClick={() => {
                navigate("/");
                setIsOpen(false);
              }}
              className="flex items-center w-full px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-all duration-200"
            >
              <Home size={16} className="mr-3" />
              Home
            </button>

            {/* Admin Link - Only show for superusers */}
            {user?.is_superuser && (
              <button
                onClick={() => {
                  navigate("/admin");
                  setIsOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-200 font-medium"
              >
                <Shield size={16} className="mr-3" />
                Admin Panel
              </button>
            )}

            {/* Settings - Commented out for now */}
            {/*
              <button
                onClick={() => setIsOpen(false)}
                className="flex items-center w-full px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-all duration-200"
              >
                <Settings size={16} className="mr-3" />
                Settings
              </button>
              */}

            <div className="border-t border-neutral-200 dark:border-neutral-700 my-1"></div>

            <button
              onClick={() => {
                handleLogout();
                setIsOpen(false);
              }}
              className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200"
            >
              <LogOut size={16} className="mr-3" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
